import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faTrash,
  faImage,
  faCloudUploadAlt,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../ApiService";

const ImageUpload = ({ onImageUpload, onUploadStart }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (file) => {
    if (file && file.type.startsWith("image/")) {
      setUploading(true);
      onUploadStart();

      try {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("uploadDir", "../temp/");

        const response = await apiService.uploadImage(formData);
        setImageUrl(response.filePath);
        onImageUpload(response.filePath, response.socialMediaPaths);
      } catch (error) {
        console.error("Error uploading image:", error.message);
        onImageUpload(null, null, true);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0]);
    }
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = async () => {
    try {
      if (imageUrl) {
        const imageFileName = imageUrl.split("/").pop();
        await apiService.removeImage(imageFileName, "../temp/");
        setImageUrl(null);
        onImageUpload(null, {
          twitter: null,
          facebook: null,
          whatsapp: null,
          linkedin: null,
        });
      }
    } catch (error) {
      console.error("Error removing image:", error.message);
    }
  };

  return (
    <div className="w-full">
      {imageUrl ? (
        /* Uploaded Image Display */
        <div className="text-center">
          <div className="inline-block bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">
                Image Uploaded Successfully!
              </h3>
              <p className="text-green-600">
                Your image has been processed and is ready for conversion.
              </p>
            </div>

            <div className="mb-6">
              <img
                src={imageUrl}
                alt="Uploaded"
                className="max-w-full h-auto max-h-64 mx-auto rounded-xl shadow-lg border"
              />
            </div>

            <button
              onClick={handleRemoveImage}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              <FontAwesomeIcon icon={faTrash} />
              Remove & Upload New
            </button>
          </div>
        </div>
      ) : (
        /* Upload Area */
        <div className="text-center">
          <div
            className={`relative border-3 border-dashed rounded-2xl p-12 transition-all duration-300 cursor-pointer ${
              dragActive
                ? "border-[#E5C558] bg-[#E5C558]/5 scale-105"
                : uploading
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 hover:border-[#E5C558] hover:bg-gray-50"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={openFileSelector}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />

            {uploading ? (
              /* Uploading State */
              <div className="space-y-4">
                <div className="w-16 h-16 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin mx-auto"></div>
                <h3 className="text-xl font-semibold text-blue-600">
                  Uploading Image...
                </h3>
                <p className="text-gray-600">
                  Please wait while we process your image
                </p>
              </div>
            ) : (
              /* Upload Prompt */
              <div className="space-y-6">
                <div className="flex justify-center">
                  <div
                    className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      dragActive
                        ? "bg-[#E5C558] text-gray-900 scale-110"
                        : "bg-gradient-to-br from-[#E5C558]/20 to-[#d4b54d]/10 text-[#E5C558]"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={dragActive ? faCloudUploadAlt : faImage}
                      className="text-3xl"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h3
                    className={`text-2xl font-bold transition-colors ${
                      dragActive ? "text-[#E5C558]" : "text-gray-900"
                    }`}
                  >
                    {dragActive ? "Drop your image here!" : "Upload Your Image"}
                  </h3>

                  <p className="text-gray-600 text-lg">
                    {dragActive
                      ? "Release to upload your image"
                      : "Drag and drop your image here, or click to browse"}
                  </p>

                  <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-500">
                    <span className="bg-gray-100 px-3 py-1 rounded-full">
                      JPG
                    </span>
                    <span className="bg-gray-100 px-3 py-1 rounded-full">
                      PNG
                    </span>
                    <span className="bg-gray-100 px-3 py-1 rounded-full">
                      GIF
                    </span>
                    <span className="bg-gray-100 px-3 py-1 rounded-full">
                      WEBP
                    </span>
                  </div>
                </div>

                {!dragActive && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-gray-900 font-bold rounded-xl hover:shadow-xl hover:shadow-[#E5C558]/30 hover:scale-105 transition-all duration-300"
                  >
                    <FontAwesomeIcon icon={faUpload} className="text-lg" />
                    Choose File
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 mb-2">
              Maximum file size: 10MB
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>100% Secure</span>
              </div>
              <div className="flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <span>Lightning Fast</span>
              </div>
              <div className="flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-purple-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Completely Free</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
